import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, CircularProgress, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QAL3SettingsForm from "./Form";
import { SETTING_TABLE_NAME } from "Constants/settingsTableName";
import { useSettingsUI, useSystemConfig } from "../../../hooks/useReduxSelectors";
import { Settings } from "../../../actions/apiActions";
import { useMsal } from "Auth/msal-context";
import { useKeyboard } from "../../../hooks/KeyboardContext";
import useSettings from "../../../hooks/useSettings";

const transformResponse = (res) => res.jsonData;

const QAL3Settings = () => {
  const { t } = useTranslation("common");
  const { isAuthorized, isEdge } = useMsal();
  const { registerInput } = useKeyboard();

  const settingsUI = useSettingsUI();
  const config = useSystemConfig();

  const [solution, setSolution] = useState(null);

  const { formState, setFormState, loading, error, fetchSettings, saveSettings, updateField, updateComponentField } =
    useSettings(SETTING_TABLE_NAME.QAL3_SETTINGS, transformResponse);

  const fetchSolution = useCallback(async () => {
    if (!config) return;
    try {
      const res = await Settings.get(config.SETTING_URL, {
        customerId: config.CUSTOMER_ID,
        deviceId: config.DEVICE_ID,
        setting: SETTING_TABLE_NAME.GENERAL_SETTING,
      });
      const parsed = JSON.parse(res.jsonData);
      setSolution(parsed?.solution ?? null);
    } catch {
      // GeneralSettings may not exist — solution stays null
    }
  }, [config]);

  const handleOpen = (e, expanded) => {
    if (expanded) {
      fetchSettings();
      fetchSolution();
    }
  };

  const onChangeHandler = useCallback((key, value, component) => {
    if (component === undefined) {
      updateField(key, value);
    } else {
      updateComponentField(component, key, value);
    }
  }, [updateField, updateComponentField]);

  const inputSelected = (e, name) => {
    const parts = name.split("|");
    if (parts.length === 2) {
      // component field: "SO2|zeroCal"
      registerInput(name, e.target.value, (v) => onChangeHandler(parts[1], v, parts[0]));
    } else {
      // top-level field: "startHour", "startMinute", "intervalWeeks"
      registerInput(name, e.target.value, (v) => onChangeHandler(name, v));
    }
  };

  const handleSave = () => {
    if (!formState) return;
    saveSettings({ ...formState });
  };

  return (
    <Accordion sx={{ mb: 2 }} disableGutters onChange={handleOpen}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "primary.main", color: "common.white" }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>QAL 3</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: "background.light", p: 2 }}>
        {loading && <CircularProgress size={24} />}
        {error && (
          <Typography sx={{ textAlign: "center", color: "secondary.main" }}>
            {t("No Settings Found")}
          </Typography>
        )}
        {!loading && formState && (
          <QAL3SettingsForm
            qal3Settings={formState}
            settingsUI={settingsUI}
            onChangeHandler={onChangeHandler}
            inputSelected={inputSelected}
            t={t}
            solution={solution}
          />
        )}
        {(isAuthorized || isEdge) && formState && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" size="small" onClick={handleSave}>{t("Save")}</Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default QAL3Settings;
